import dayjs from "dayjs";
import { FieldValue as FieldValueFull } from "firebase/firestore";
import { FieldValue as FieldValueLite } from "firebase/firestore/lite";
import type {
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  GeometryCollection,
} from "geojson";
import * as linkify from "linkifyjs";
import React, { useCallback } from "react";
import type { LocalSurvey } from "senf-atomic-design-system/src/components/special/survey/survey.config";
import type { Project } from "../types";
import {
  ageOptions,
  resolveAgeToDetailedGroupKey,
  sentimentOptions,
} from "./constants/constants";
import i18n from "./i18n";

export const pipe =
  <T>(...fns: Array<(x: T) => T>) =>
  (x: T): T =>
    fns.reduce((v, f) => f(v), x);

/*
https://arizonatribe.github.io/vanillas/global.html#pick

Removes everything except the specified keys from an object (after cloning the Object).

@param keys 	Array.<String> 	 An array of keys to search for in the Object and include from the output
@param obj 	  Object 	         An Object from which to copy and remove keys

@returns A copy of the original Object, but with only the specified keys
*/
export const pick = (keys, obj) => {
  const newObj = {};
  const numOfKeys = keys.length;
  for (let i = 0; i < numOfKeys; i++) {
    if (keys[i] in obj) {
      newObj[keys[i]] = obj[keys[i]];
    }
  }
  return newObj;
};

export const openLink = (weblink: string) => {
  const convertedLinkRaw = weblink && linkify.find(weblink);
  const convertedLink =
    weblink && convertedLinkRaw[0] !== undefined && convertedLinkRaw[0].href;

  window.open(convertedLink, "_blank");
};
export const openMail = (contact: string, subject?: string, body?: string) => {
  if (contact && subject && body) {
    openLink(`mailto:${contact}?subject=${subject}&body=${body}`);
  } else if (contact && subject) {
    openLink(`mailto:${contact}?subject=${subject}`);
  } else if (contact) {
    openLink(`mailto:${contact}`);
  }
};

export const todayIsWithinDateRange = (
  startDate: string | Date,
  endDate: string | Date,
) => {
  if (!startDate || !endDate) {
    return false;
  }
  const today = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Set end date's time to the last possible moment of the day (23:59:59.999)
  end.setHours(23, 59, 59, 999);

  // Remove time component from the current date to compare only the dates
  start.setHours(0, 0, 0, 0);
  return today >= start && today <= end;
};

export const getPhaseOfDateRange = (
  startDate: string | Date,
  endDate: string | Date,
): "before" | "while" | "after" | "notYetScheduled" => {
  if (!startDate || !endDate) {
    return "notYetScheduled";
  }

  const today = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Set end date's time to the last possible moment of the day (23:59:59.999)
  end.setHours(23, 59, 59, 999);
  start.setHours(0, 0, 0, 0);

  // Remove time component from the current date to compare only the dates
  today.setHours(0, 0, 0, 0);

  if (today < start) {
    return "before";
  }
  if (today > end) {
    return "after";
  }
  return "while";
};

export const useTraceUpdate = (props) => {
  const prev = React.useRef(props);
  React.useEffect(() => {
    const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
      if (prev.current[k] !== v) {
        ps[k] = [prev.current[k], v];
      }
      return ps;
    }, {});
    if (Object.keys(changedProps).length > 0) {
      console.log("Changed props:", changedProps);
    }
    prev.current = props;
  });
};

export const generateLinkWithOrWithoutSlug = (projectId?: string) => {
  const navigationPath = window.location.pathname.includes("/projects/")
    ? `/projects/${projectId}`
    : "";
  return navigationPath;
};

/**
 * System domains used for identifying subdomains
 */
export const SYSTEM_DOMAINS = [
  "senf.app",
  "demo.senf.app",
  "participate.app",
  "demo.participate.app",
  "mitmachen.app",
  "demo.mitmachen.app",
  "netlify.app",
  "localhost",
];

/**
 * Determines if a domain is a custom subdomain or an external domain
 * @returns "custom" for subdomains (e.g., project-name.senf-custom-domains.netlify.app)
 * @returns "external" for external domains (e.g., masterplan-brooklyn.com)
 */
export const getDomainType = (domain: string): "custom" | "external" => {
  if (!domain) return "external";
  const normalized = domain.toLowerCase().trim();
  const isSubdomain = SYSTEM_DOMAINS.some(
    (sys) => normalized.endsWith(`.${sys}`) || normalized === sys,
  );
  return isSubdomain ? "custom" : "external";
};

/**
 * Maps a primaryDomain value to a human-readable brand name for og:site_name.
 * Handles original format ("senf.app/projects/xxx"), custom subdomains, and external domains.
 */
export const getOgSiteName = (primaryDomain: string): string => {
  const hostname = primaryDomain.split("/")[0].toLowerCase();
  if (hostname === "senf.app" || hostname === "demo.senf.app")
    return "Senf.app";
  if (hostname.endsWith(".participate.app") || hostname === "participate.app")
    return "Participate.app";
  if (hostname.endsWith(".mitmachen.app") || hostname === "mitmachen.app")
    return "Mitmachen.app";
  return hostname.charAt(0).toUpperCase() + hostname.slice(1);
};

/**
 * Gets the base domain URL based on environment
 */
export const getBaseDomain = () => {
  const env = import.meta.env.VITE_APP_MODE;
  if (env === "development") {
    return { protocol: "http", domain: "localhost:3000" };
  }
  if (env === "production") {
    return { protocol: "https", domain: "senf.app" };
  }
  return { protocol: "https", domain: "demo.senf.app" };
};

/**
 * When copying/sharing a survey or participation link in development, the link
 * should point to the app where participants open it (the other localhost port).
 * E.g. when on localhost:3000 copy localhost:3001, when on localhost:3001 copy localhost:3000.
 * In staging and production the current origin is used (no swap).
 */
const getSharedLinkOrigin = (): string | null => {
  if (typeof window === "undefined") return null;
  const env = import.meta.env.VITE_APP_MODE;
  if (env !== "development") return null;
  const { origin } = window.location;
  if (origin === "http://localhost:3000") return "http://localhost:3001";
  if (origin === "http://localhost:3001") return "http://localhost:3000";
  return null;
};

/**
 * Generate a short unique identifier (5-7 chars) from projectId
 */
export const getShortProjectId = (id: string): string => {
  // Simple hash function to distribute characters evenly
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to base36 (0-9, a-z) for additional uniqueness
  const base36 = Math.abs(hash).toString(36);

  // Take characters from different positions in projectId + hash for uniqueness
  const chars = id.split("");
  const shortId = [
    chars[0] || "a",
    chars[Math.floor(id.length / 3)] || "b",
    chars[Math.floor((id.length * 2) / 3)] || "c",
    chars[id.length - 1] || "d",
    base36[0] || "e",
    base36[Math.floor(base36.length / 2)] || "f",
    base36[base36.length - 1] || "g",
  ]
    .filter(Boolean)
    .join("")
    .substring(0, 7)
    .padEnd(5, base36[0] || "0"); // Ensure at least 5 chars

  return shortId.length >= 5 && shortId.length <= 7
    ? shortId
    : shortId.substring(0, 7);
};

/**
 * Convert a stored domain to localhost format for development
 * Only converts demo.senf.app and subdomains of participate.app/mitmachen.app, not senf.app (production)
 */
const convertToLocalhost = (domain: string): string => {
  // Normalize legacy long dev format back to short subdomain.localhost:3000
  const longParticipate = domain.match(
    /^(.+)\.demo\.participate\.app\.localhost(?::\d+)?$/,
  );
  if (longParticipate) {
    return `${longParticipate[1]}.localhost:3000`;
  }
  const longMitmachen = domain.match(
    /^(.+)\.demo\.mitmachen\.app\.localhost(?::\d+)?$/,
  );
  if (longMitmachen) {
    return `${longMitmachen[1]}.localhost:3000`;
  }

  // Convert demo.senf.app to localhost:3000 (e.g. project-xxx.demo.senf.app -> project-xxx.localhost:3000)
  if (domain.includes("demo.senf.app")) {
    return domain.replace(/demo\.senf\.app/g, "localhost:3000");
  }

  // project-xxx.demo.participate.app -> project-xxx.localhost:3000
  const participateMatch = domain.match(/^(.+)\.demo\.participate\.app$/);
  if (participateMatch) {
    return `${participateMatch[1]}.localhost:3000`;
  }

  // projekt-xxx.demo.mitmachen.app -> projekt-xxx.localhost:3000
  const mitmachenMatch = domain.match(/^(.+)\.demo\.mitmachen\.app$/);
  if (mitmachenMatch) {
    return `${mitmachenMatch[1]}.localhost:3000`;
  }

  return domain;
};

/**
 * Generate initial domain settings (original URL + short subdomain) for a project
 * Always stores demo.senf.app (not localhost) for dev/staging.
 * The short subdomain (e.g. `project-abc12.participate.app` / `projekt-abc12.mitmachen.app`)
 * is used as the initial `primaryDomain`; the `*.senf.app/projects/<id>` URL is still
 * kept in `domains` so existing links/sync keep working.
 */
export const generateInitialDomains = (
  projectId: string,
  baseDomain: string,
  language?: string,
): { primaryDomain: string; domains: string[] } => {
  const env = import.meta.env.VITE_APP_MODE;
  // Always use demo.senf.app for dev/staging, senf.app for production
  const storageDomain = env === "production" ? "senf.app" : "demo.senf.app";
  const originalUrl = `${storageDomain}/projects/${projectId}`;
  const shortId = getShortProjectId(projectId);
  const projectPrefix = language === "de" ? "projekt" : "project";
  const shortProjectUrl = `${projectPrefix}-${shortId}${baseDomain}`;

  return {
    primaryDomain: shortProjectUrl,
    domains: [originalUrl, shortProjectUrl],
  };
};

/**
 * Applies the shared-link origin in development (localhost port swap for copy link).
 * Only rewrites when the URL is already localhost. In staging/prod returns the URL unchanged.
 */
const applySharedLinkOrigin = (url: string): string => {
  const targetOrigin = getSharedLinkOrigin();
  if (!targetOrigin) return url;
  try {
    const parsed = new URL(url);
    const isLocalhost =
      parsed.hostname === "localhost" &&
      (parsed.port === "3000" || parsed.port === "3001");
    if (!isLocalhost) return url;
    return `${targetOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

/** Query param used to persist test-db in shared links */
export const TEST_DB_PARAM = "testDb";
/** localStorage key for test DB (Firebase) mode in dev/staging */
export const TEST_DB_STORAGE_KEY = "test-db";

/** Participant app port in local dev (senf-client). */
const DEV_PARTICIPANT_LINK_PORT = "3000";

/**
 * Rewrites a URL for copying so it opens in the right environment.
 * In development, participant module links keep the project subdomain (*.localhost:3000)
 * and only normalize the port to the client app. Plain localhost URLs without a subdomain
 * are rewritten to localhost:3000 (path only) when copied from the dashboard (3001).
 */
export const toCurrentEnvironmentUrl = (url: string): string => {
  if (typeof window === "undefined") return url;
  const env = import.meta.env.VITE_APP_MODE;
  if (env !== "development") return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.hostname.endsWith(".localhost")) {
      parsed.protocol = "http:";
      parsed.port = DEV_PARTICIPANT_LINK_PORT;
      return `${parsed.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return `http://localhost:${DEV_PARTICIPANT_LINK_PORT}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

/**
 * Adds or updates the testDb query param on a URL when test-db is enabled.
 * Only applies in dev/staging when localStorage test-db is "true".
 */
export const addTestDbParamToUrl = (url: string): string => {
  if (typeof window === "undefined") return url;
  const env = import.meta.env.VITE_APP_MODE;
  if (env !== "development" && env !== "staging") return url;
  try {
    const isTestDb = localStorage.getItem(TEST_DB_STORAGE_KEY) === "true";
    const parsed = new URL(url, window.location.origin);
    if (isTestDb) {
      parsed.searchParams.set(TEST_DB_PARAM, "true");
    } else {
      parsed.searchParams.delete(TEST_DB_PARAM);
    }
    return parsed.href;
  } catch {
    return url;
  }
};

/**
 * Prepares a link for copying: current environment origin + testDb param when applicable.
 * Call this before writing a survey/infopage/idea map link to the clipboard.
 */
export const prepareLinkForCopy = (url: string): string => {
  const withOrigin = toCurrentEnvironmentUrl(url);
  try {
    return addTestDbParamToUrl(withOrigin);
  } catch {
    return withOrigin;
  }
};

/**
 * Generates a project link based on domain configuration and primary domain preference.
 * @param typeMap Optional path after the project root (e.g. `survey/id`, `info`). Omit or pass `""` for the base project URL only.
 */
export const generateLink = (
  project: Project,
  typeMap = "",
  withProjectIdSlug = true,
) => {
  const env = import.meta.env.VITE_APP_MODE;
  const { protocol, domain } = getBaseDomain();
  const projectId = project.projectId || "";

  // Build legacy URL (original domain with projectId slug)
  const projectPath = withProjectIdSlug ? `projects/${projectId}/` : "";
  const legacyUrl = `${protocol}://${domain}/${projectPath}${typeMap}`;

  // Use primaryDomain if available
  if (project.primaryDomain) {
    // Check if primaryDomain is an original domain format (contains /projects/)
    const isOriginalDomainFormat = project.primaryDomain.includes("/projects/");
    // Check if it's a domain that should be converted in dev (demo.senf.app or subdomains of participate.app/mitmachen.app)
    const isDemoDomain = project.primaryDomain.includes("demo.senf.app");
    const isParticipateSubdomain =
      project.primaryDomain.includes(".participate.app");
    const isMitmachenSubdomain =
      project.primaryDomain.includes(".mitmachen.app");
    const shouldConvertInDev =
      isDemoDomain || isParticipateSubdomain || isMitmachenSubdomain;

    if (isOriginalDomainFormat) {
      // Original domain format: demo.senf.app/projects/xxx or senf.app/projects/xxx
      if (env === "development" && shouldConvertInDev) {
        // Convert demo.senf.app to localhost:3000/projects/xxx/typeMap
        const localhostDomain = convertToLocalhost(project.primaryDomain);
        return applySharedLinkOrigin(`http://${localhostDomain}/${typeMap}`);
      }
      // For senf.app or non-dev, use as-is
      return applySharedLinkOrigin(
        `https://${project.primaryDomain}/${typeMap}`,
      );
    }

    const domainType = getDomainType(project.primaryDomain);
    let domainToUse = project.primaryDomain;

    // Convert stored domain to localhost format if in development and it should be converted
    if (env === "development" && shouldConvertInDev) {
      domainToUse = convertToLocalhost(project.primaryDomain);
    }

    if (domainType === "external") {
      // External domains - don't convert, use as-is
      return applySharedLinkOrigin(
        `https://www.${project.primaryDomain}/${typeMap}`,
      );
    }

    if (domainType === "custom") {
      // Custom subdomain format: project-xxx.demo.senf.app, project-xxx.demo.participate.app, etc.
      if (env === "development" && shouldConvertInDev) {
        return applySharedLinkOrigin(`http://${domainToUse}/${typeMap}`);
      }
      return applySharedLinkOrigin(
        `https://${project.primaryDomain}/${typeMap}`,
      );
    }

    // Fallback to legacy URL
    return applySharedLinkOrigin(legacyUrl);
  }

  // Fallback to legacy URL if no primaryDomain
  return applySharedLinkOrigin(legacyUrl);
};

export function truncateString(str: string, num: number) {
  if (!str) return str;
  if (str.length > num) {
    return `${str.substring(0, num)}...`;
  }
  return str;
}

// export const generateHash = (string) => {
//   let hash = 0;
//   if (string.length == 0) return hash;
//   for (let i = 0; i < string.length; i++) {
//     const charCode = string.charCodeAt(i);
//     hash = (hash << 7) - hash + charCode;
//     hash &= hash;
//   }
//   return hash;
// };

export const getNumericId = (uid: string) => {
  // Take first 8 chars and convert to numeric value
  return Number.parseInt(uid.slice(0, 8), 36).toString().slice(0, 6);
};
export const getUserHandle = (
  isAnonymous: boolean,
  uid: string,
  userHandle: string,
) => {
  return uid && (isAnonymous || !userHandle)
    ? `${i18n.t("global:guest")}-${getNumericId(uid)}`
    : userHandle;
};

export function generateHash(input) {
  const str = String(input);
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const charCode = str.charCodeAt(i);
    hash = (hash << 5) - hash + charCode;
    hash &= hash;
  }
  return hash;
}

export function unhash(hash) {
  // Implement a hash function that converts the hash back to its original state
  // For example, you can use the implementation of the hash function that you provided earlier:
  const string = hash.toString();
  let hashVal = 0;
  for (let i = 0; i < string.length; i++) {
    const char = string.charCodeAt(i);
    hashVal = (hashVal << 5) - hashVal + char;
    hashVal &= hashVal;
  }
  return hashVal;
}

export function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): T {
  let lastRun = 0;

  return ((...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastRun >= delay) {
      lastRun = now;
      return func(...args);
    }
  }) as T;
}

export const CATEGORY_COLORS = [
  "#bd98f6",
  "#91dff4",
  "#929df6",
  "#8dd9b8",
  "#e8907e",
  "#f6c095",
  "#f9db95",
] as const;

export const CATEGORY_COLOR_OPTIONS: ReadonlyArray<{ color: string }> =
  CATEGORY_COLORS.map((color) => ({ color }));

export function topicsArrayToObject(topics) {
  return topics.reduce((acc, { value, label, color }) => {
    return { ...acc, [value]: { label, color } };
  }, {});
}
export function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatDateBasedOnLanguage(date: Date, language: string) {
  const convertedDate = new Date(date);
  if (isNaN(convertedDate.getTime())) {
    console.error("Invalid date provided");
    return;
  }

  const dateOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  };

  // Use the provided language parameter directly.
  // Most environments will default to English (or the environment's default locale)
  // if the specified language/locale is not supported.
  try {
    return convertedDate.toLocaleDateString(language, dateOptions);
  } catch (e) {
    console.error(
      `Locale '${language}' not supported. Falling back to default.`,
    );
    // You can specify a default locale here if desired, or simply return undefined or an error message.
    return convertedDate.toLocaleDateString("en-US", dateOptions); // Fallback to English (US)
  }
}

/**
 * Same rules as `RangeDate` (admin/survey lists): when both dates share a year, the start uses `DD.MM.`;
 * otherwise both use `format`. With only one bound, that date is formatted with `format` only.
 */
export function formatRangeDate(
  startDate: string | Date | undefined | null,
  endDate: string | Date | undefined | null,
  format = "DD.MM.YYYY",
): string {
  const fmtOne = (v: string | Date) => {
    const d = dayjs(v);
    return d.isValid() ? d.format(format) : "";
  };
  const hasStart = startDate != null && String(startDate).trim() !== "";
  const hasEnd = endDate != null && String(endDate).trim() !== "";
  if (!hasStart && !hasEnd) return "";
  if (hasStart && !hasEnd) return fmtOne(startDate as string | Date);
  if (!hasStart && hasEnd) return fmtOne(endDate as string | Date);

  const end = dayjs(endDate);
  const start = dayjs(startDate);
  if (!start.isValid() || !end.isValid()) return "";
  const startFormat = end.isSame(start, "year") ? "DD.MM." : format;
  return `${start.format(startFormat)} – ${end.format(format)}`;
}

export function addModeToSessionStorage() {
  const searchParams = new URLSearchParams(window.location.search);
  const mode = searchParams.get("mode");

  if (mode === "workshop") {
    sessionStorage.setItem("mode", mode);
    return;
  }

  sessionStorage.removeItem("mode");
}

export function getModeFromSessionStorage() {
  const mode = sessionStorage.getItem("mode");
  return mode;
}

/**
 * Converts a number of bytes to a human-readable file size string with a specified number of decimal places.
 *
 * @param {number} bytes - The number of bytes to convert.
 * @param {number} [decimals=2] - The number of decimal places to display in the result (default is 2).
 * @returns {string} The formatted file size string.
 *
 * @note According to JEDEC standartization 1kB = 1000 Bytes, 1KB = 1024 Bytes. 1 KiB is also 1024 Bytes but not used here because it is far less common.
 */
export function formatBytes(
  bytes: number,
  options?: {
    decimals?: number;
    si?: boolean;
  },
): string {
  const { decimals = 2, si = false } = options || {};
  if (!bytes) return "0 Bytes";

  const k = si ? 1000 : 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}
// Helper function for delaying execution
export const delay = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const isURL = (str: string) => {
  // Regular expression to match URL pattern
  const urlPattern =
    /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(:\d{1,5})?([/?#]\S*)?$/i;

  return urlPattern.test(str);
};

export const setFocus = (
  index: number,
  select = false,
  active = document.activeElement as HTMLInputElement,
) => {
  if (!active) return;

  // Get all the input elements in the document
  const inputElements = document.querySelectorAll(
    "input, select, textarea, button, [tabindex]",
  );

  // Find the index of the currently focused input element
  const currentIndex = Array.from(inputElements).findIndex(
    (el) => el === active,
  );

  // Find the next input element
  let nextElement = inputElements[currentIndex + index] as HTMLInputElement;
  while (nextElement && nextElement.disabled) {
    nextElement = inputElements[
      currentIndex + index + index
    ] as HTMLInputElement;
  }

  // Focus on the next input element
  if (nextElement) {
    nextElement.focus();
    if (select && typeof nextElement.select === "function")
      nextElement.select();
  } else {
    active.blur();
  }
};

export function parseGeoData<
  T extends FeatureCollection<Geometry | GeometryCollection, GeoJsonProperties>,
>(geoData?: string | null | Record<string, any>): T | null {
  if (!geoData) return null;

  if (typeof geoData === "string") {
    // Check if the string is a URL
    const urlPattern = /^(http|https):\/\/[^ "]+$/;
    if (urlPattern.test(geoData.trim())) {
      // It's a URL, not JSON - return null to indicate it needs to be fetched
      return null;
    }
    // Try to parse as JSON
    try {
      return JSON.parse(geoData);
    } catch (error) {
      // If parsing fails, return null
      console.warn("Failed to parse geoData as JSON:", error);
      return null;
    }
  }

  return geoData as T;
}

export const getInitialsColor = (
  inputText: string | number,
  type = "tailwind",
  usage = "bg",
) => {
  // Special case for "other"
  if (inputText === "other") {
    return type === "tailwind" ? `${usage}-category-surface-yellow` : "#e1ad35";
  }

  let text;
  // Check if the text is a number and if so convert it to its corresponding letter
  if (typeof inputText === "number") {
    text =
      inputText === -1
        ? "e"
        : String.fromCharCode(65 + Number.parseInt(inputText, 10));
  } else {
    // Get the last character of the input text, or 'e' if empty/undefined
    text = inputText?.slice(-1) || "e";
  }

  const colors =
    type === "tailwind"
      ? [
          `${usage}-category-surface-rose`,
          `${usage}-category-surface-green`,
          `${usage}-category-surface-teal`,
          `${usage}-category-surface-blue`,
          `${usage}-category-surface-purple`,
          `${usage}-category-surface-red`,
          `${usage}-category-surface-orange`,
          `${usage}-category-surface-brown`,
          `${usage}-tag-text-green`,
          `${usage}-tag-text-teal`,
          `${usage}-tag-text-blue`,
          `${usage}-tag-text-purple`,
          `${usage}-tag-text-rose`,
          `${usage}-tag-text-red`,
          `${usage}-tag-text-orange`,
          `${usage}-tag-text-brown`,
        ]
      : [
          "#e67eab",
          "#5ab15f",
          "#3dadbb",
          "#809af9",
          "#b588ff",
          "#f07e67",
          "#e4892d",
          "#cc9365",
          "#265e2a",
          "#0a5b65",
          "#354ca1",
          "#60438f",
          "#883559",
          "#92321f",
          "#7e4308",
          "#724926",
        ];

  const colorIndex = text.toLowerCase().charCodeAt(0) % colors.length;

  return colors[colorIndex];
};

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "14px verdana").
 *
 * @see http://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export function getTextWidth(text: string, font: string): number {
  // Define a type-safe cache for the canvas
  if (!getTextWidth.canvas) {
    getTextWidth.canvas = document.createElement("canvas");
  }

  const context = getTextWidth.canvas.getContext("2d");
  if (!context) {
    console.error("Failed to get 2D context");
    return (
      text.length * Number.parseFloat(font.split(" ")[0].replace("px", ""))
    );
  }

  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

export function getTextLines(
  text: string,
  options?: {
    font?: string;
    width?: number;
    lineHeight?: string;
  },
): number {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context || !text) return 0;
  context.font = options?.font ?? "16px Nunito";

  const words = text.replace(/&#x20;/g, "").split(/\s+/);
  let line = "";
  let lines = 0;

  for (let n = 0; n < words.length; n++) {
    const testLine = line + (line ? " " : "") + words[n];
    const metrics = context.measureText(testLine);
    const testWidth = metrics.width;

    if (testWidth > (options?.width ?? 100) && line) {
      lines++;
      line = words[n]; // start new line with current word
    } else {
      line = testLine;
    }
  }

  if (line) lines++;

  return lines;
}

export const getLongestLabel = <T extends Record<string, any>>(
  array: T[],
  getter: (item: T) => string | undefined,
  options: {
    fontSize?: string;
    padding?: number;
    minWidth?: number;
    split?: boolean;
  } = {},
) => {
  const {
    fontSize = "normal 16px Nunito",
    padding = 28,
    minWidth = 72,
  } = options;
  return array
    .flatMap((e) => {
      const label = getter(e) || "";
      return options.split ? label.split(/[\s/]+/) : [label];
    })
    .reduce((acc, e) => {
      return Math.max(acc, getTextWidth(e, fontSize) + padding);
    }, minWidth);
};

export namespace getTextWidth {
  export let canvas: HTMLCanvasElement | undefined;
}
/**
 * Generates an alphabetic representation for a given numeric input.
 *
 * This function converts a numeric input into a corresponding alphabetic representation
 * based on the position in the English alphabet. For example, 1 is converted to 'A',
 * 2 to 'B', 27 to 'AA', and so on.
 *
 */
export const getAlpha = (i: number): string => {
  const num = i + 1;
  const mod = num % 26 || 26;
  const pow = Math.floor(num / 27);
  const out = String.fromCharCode(64 + mod);

  if (pow && i < 999) return getAlpha(pow - 1) + out;
  return out;
};

export function getSentimentName(sentiment) {
  // if (!age) {
  //   return "noInformation";
  // }
  for (const option of sentimentOptions) {
    if (sentiment >= option.lowerBound && sentiment <= option.upperBound) {
      return option.value;
    }
  }
}

export function getAgeGroupName(age) {
  if (!age) {
    return "noInformation";
  }
  for (const option of ageOptions) {
    if (age >= option.lowerBound && age <= option.upperBound) {
      return option.value;
    }
  }
}

export const getAgeGroup = (age: number) =>
  resolveAgeToDetailedGroupKey(age);

export function calculateSubmissionWeek(createdAt, startDate, endDate, t) {
  // Convert the date strings to Date objects
  const createdAtDate = new Date(createdAt);
  const startDateDate = new Date(startDate);
  const endDateDate = new Date(endDate);

  // Check if the createdAt date is within the specified range
  if (createdAtDate >= startDateDate && createdAtDate <= endDateDate) {
    // Calculate the difference in milliseconds between createdAt and startDate
    const timeDifference = createdAtDate - startDateDate;

    // Calculate the number of weeks by dividing the time difference by the number of milliseconds in a week
    const weekNumber = Math.ceil(timeDifference / (7 * 24 * 60 * 60 * 1000));

    // Use the translation function to display the week number
    return `${t("global:week")} ${weekNumber}`;
  }
  // Return a message indicating that the createdAt date is outside the specified range
  return t("global:invalidDate");
}

/**
 * Generates a localized filter suffix string from active filters for use in filenames.
 * Returns an empty string if no filters are active.
 *
 * @param activeFilters - Array of active filter objects with filterName and selectedFilters
 * @param t - Translation function (from useTranslation hook)
 * @param survey - Survey object to resolve tag filter labels from evaluation tableColumns
 * @returns A sanitized, localized string representing the active filters, or empty string if none
 */
export function getFilterSuffix(
  activeFilters?: Array<{
    filterName: string;
    selectedFilters: string[];
    questionId?: string;
  }>,
  t?: (key: string) => string,
  survey?: {
    pages?: Array<{
      evaluation?: {
        tableColumns?: Array<{
          value: string;
          label: string;
          values?: Array<{
            value: string;
            label: string;
          }>;
        }>;
      };
    }>;
  },
): string {
  if (!activeFilters || activeFilters.length === 0) {
    return "";
  }

  const normalizeAndSanitize = (str: string | number | null | undefined) =>
    String(str ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\u2013/g, "-") // Convert en dash to hyphen
      .replace(/\u2014/g, "-") // Convert em dash to hyphen
      .replace(/[^a-zA-Z0-9_-]/g, "")
      .replace(/ /g, "_")
      .replace(/-+/g, "-")
      .replace(/_+/g, "_");

  // Map filter names to translation keys
  const getFilterLabel = (filterName: string): string => {
    const labelMap: Record<string, string> = {
      completed: t?.("global:completed") || "completed",
      ageGroup: t?.("global:ageGroup") || "ageGroup",
      gender: t?.("global:gender") || "gender",
      postcode: t?.("global:postcode") || "postcode",
      lang: t?.("global:language") || "lang",
      geoOptions: t?.("global:options") || "options",
      topic: t?.("global:topic") || "topic",
      localityName: t?.("global:locality") || "locality",
    };
    return labelMap[filterName] || filterName;
  };

  // Resolve age group value to label (e.g., "ageGroup-4" -> "35-44")
  const getAgeGroupLabel = (value: string): string => {
    if (value === "noInformation") {
      return t?.("global:noInformation") || "noInformation";
    }
    // Use existing ageOptions array to find the text label
    const ageOption = ageOptions.find((option) => option.value === value);
    return ageOption?.text || value;
  };

  // Resolve gender value to translated label
  const getGenderLabel = (value: string): string => {
    return t?.(`global:${value}`) || value;
  };

  // Resolve tag filter value to label from survey evaluation subcollection (evaluationByPageUid)
  const getTagLabel = (filterName: string, tagUid: string): string | null => {
    if (!survey?.pages) return null;
    const evaluationByPageUid = (survey as LocalSurvey).evaluationByPageUid;
    if (!evaluationByPageUid) return null;
    for (const page of survey.pages) {
      const pageUid = (page as { uid?: string }).uid;
      if (!pageUid) continue;
      const tableColumns = evaluationByPageUid[pageUid]?.tableColumns;
      const column = tableColumns?.find((col) => col.value === filterName);
      if (column) {
        const tagValue = column.values?.find((v) => v.value === tagUid);
        return tagValue?.label || null;
      }
    }
    return null;
  };

  const filterParts: string[] = [];

  for (const filter of activeFilters) {
    // Skip cross filters as they can be complex
    if (filter.filterName.startsWith("crossFilter_")) {
      continue;
    }

    const filterLabel = normalizeAndSanitize(getFilterLabel(filter.filterName));
    const selectedValues = filter.selectedFilters;

    if (selectedValues.length === 0) continue;

    // Resolve filter values to readable labels
    const valueLabels: string[] = [];
    for (const value of selectedValues) {
      let label: string;
      if (filter.filterName === "ageGroup") {
        label = getAgeGroupLabel(value);
      } else if (filter.filterName === "gender") {
        label = getGenderLabel(value);
      } else if (filter.filterName === "completed") {
        label =
          value === "true"
            ? t?.("global:completed") || "completed"
            : t?.("global:notCompleted") || "notCompleted";
      } else if (filter.filterName === "lang") {
        // Language codes are usually fine as-is, but could be translated
        label = value;
      } else if (filter.filterName === "postcode") {
        label = value;
      } else {
        // Try to resolve as tag filter
        const tagLabel = getTagLabel(filter.filterName, value);
        label = tagLabel || value;
      }
      valueLabels.push(normalizeAndSanitize(label));
    }

    // Format: "FilterLabel-Value1-Value2" or just "FilterLabel-Value" for single value
    const valuePart =
      valueLabels.length === 1
        ? valueLabels[0]
        : valueLabels.slice(0, 5).join("/"); // Limit to 3 values max
    filterParts.push(`${filterLabel}-${valuePart}`);
  }

  // Limit total filters to prevent very long filenames
  const limitedParts = filterParts.slice(0, 5);
  return limitedParts.length > 0 ? `${limitedParts.join("_")}` : "";
}

export function createSafeFilename(
  parts: (string | undefined)[],
  maxLength = 250,
) {
  const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  /** Snake_case segments: spaces and punctuation become underscores, then unsafe chars removed (cross-platform filenames). */
  const normalizeAndSanitize = (str: string): string => {
    return (
      str
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ß/g, "ss")
        // Word boundaries: whitespace and common punctuation → underscore (must run before stripping)
        .replace(/[\s._\-–—,;:/\\|'"´`!?()[\]{}&@#+%^*=~<>]+/g, "_")
        .replace(/[^a-zA-Z0-9_]/g, "")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "")
    );
  };
  const sanitizedParts = parts
    .map((part) => {
      if (part == null || typeof part !== "string") return undefined;
      const s = normalizeAndSanitize(part);
      return s.length > 0 ? s : undefined;
    })
    .filter((e): e is string => typeof e === "string");
  sanitizedParts.push(currentDate);
  const filteredParts = sanitizedParts;
  const maxPartLength = Math.floor(maxLength / filteredParts.length);

  return filteredParts
    .map((e) => {
      if (e.length > maxPartLength)
        return `${e.slice(0, maxPartLength - 3)}...`;
      return e;
    })
    .join("_");
}

export function fixFalslyTranslatedMarkdown(text) {
  if (typeof text !== "string") {
    return text;
  }

  let fixedMarkdown = text;

  // Remove <u> and </u> tags, preserving the content between them
  fixedMarkdown = fixedMarkdown.replace(/<u>|<\/u>/gi, "");

  // Make > possible
  fixedMarkdown = fixedMarkdown.replace(/>/g, "&gt;");

  // Replace all occurrences of '&#x20;' with nothing
  fixedMarkdown = fixedMarkdown.replace(/&#x20;/g, "");

  // Remove empty bold markers (e.g., **** which is bold with no content)
  // This can happen when users delete bold text but leave the markers behind
  fixedMarkdown = fixedMarkdown.replace(/\*{4,}/g, "");

  // Replace all occurrences of '** ' with '**'
  // fixedMarkdown = fixedMarkdown.replace(/(^|\n)\*\* /g, "$1**");

  // Replace all occurrences of ' **' with '**'
  // fixedMarkdown = fixedMarkdown.replace(/ \*\*(?=\W|$)/g, "**");

  // Replace ';' with '**' if it is at the end of a line
  // fixedMarkdown = fixedMarkdown.replace(/;(\r?\n)/g, "**$1");
  // Handle unclosed bold markers '**'
  fixedMarkdown = fixedMarkdown.replace(
    /(\*\*[\s\S]*?)(?:(\*\*)|(\r?\n|$))/g,
    (match, p1, p2, p3) => {
      if (p2 === "**") {
        return match; // If it ends with '**', return as is
      }
      return `${p1}**${p3 || ""}`; // If it does not end with '**', add it and maintain line breaks
    },
  );

  return fixedMarkdown;
}
export const getSum = (arr: number[]) => {
  if (arr.length === 0) return 0;
  return arr.reduce((acc, curr) => acc + curr, 0);
};

export const getAverage = (arr: number[]) =>
  arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

export const getMedian = (arr: number[]) => {
  if (arr.length === 0) return 0;
  const sorted = arr.sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

export const round = (num: number, decimals = 2) => {
  return Math.round(num * 10 ** decimals) / 10 ** decimals;
};

export type DeepComparable =
  | string
  | number
  | boolean
  | null
  | undefined
  | DeepComparable[]
  | { [key: string]: DeepComparable };

/**
 * Deep equality comparison function to replace lodash's isEqual
 * Performs a deep comparison between two values to determine if they are equivalent.
 * @param a First value to compare
 * @param b Second value to compare
 * @returns boolean indicating if the values are deeply equal
 */
export function isEqual(a: DeepComparable, b: DeepComparable): boolean {
  // Handle simple cases first
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return a === b;

  // Handle arrays
  if (Array.isArray(a)) {
    if (!Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }
  if (Array.isArray(b)) return false;

  // Handle objects
  const objA = a as Record<string, DeepComparable>;
  const objB = b as Record<string, DeepComparable>;

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(
    (key) => Object.hasOwn(objB, key) && isEqual(objA[key], objB[key]),
  );
}

export const getFileTypeLabel = (
  n = 1,
  fileTypes?: string[],
  explicit?: { singular: string; plural: string },
) => {
  if (explicit) {
    return n === 1 ? explicit.singular : explicit.plural;
  }
  if (!fileTypes) {
    return n === 1 ? "global:file" : "global:files";
  }
  if (fileTypes.every((type) => type.startsWith("image/"))) {
    return n === 1 ? "global:image" : "global:images";
  }
  if (fileTypes.every((type) => type.startsWith("video/"))) {
    return n === 1 ? "global:video" : "global:videos";
  }
  return n === 1 ? "global:file" : "global:files";
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

/** HTML entities → plain text (iterates so `&amp;#x20;` becomes a space). */
const decodeHtmlEntitiesForPlainText = (raw: string): string => {
  let s = raw;
  for (let pass = 0; pass < 4; pass++) {
    const before = s;
    s = s
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&#x([0-9a-fA-F]+);/gi, (m, hex: string) => {
        const cp = Number.parseInt(hex, 16);
        if (Number.isNaN(cp) || cp < 0 || cp > 0x10ffff) return m;
        try {
          return String.fromCodePoint(cp);
        } catch {
          return m;
        }
      })
      .replace(/&#([0-9]{1,7});/g, (m, dec: string) => {
        const cp = Number.parseInt(dec, 10);
        if (Number.isNaN(cp) || cp < 0 || cp > 0x10ffff) return m;
        try {
          return String.fromCodePoint(cp);
        } catch {
          return m;
        }
      })
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'");
    if (s === before) break;
  }
  return s;
};

/** Inline/markdown tokens → plain text (single paragraph). */
const stripMarkdownInline = (text: string): string => {
  if (!text) return "";

  let s = decodeHtmlEntitiesForPlainText(text);

  // Normalize all line-ending variants to space so `.` in regexes can match across them
  s = s.replace(/[\r\n\u2028\u2029]/g, " ");

  // Normalize non-ASCII asterisk look-alikes (fullwidth, operator, low) to ASCII
  s = s.replace(/[\uFF0A\u2217\u204E]/g, "*");

  return s
    .replace(/~~(.*?)~~/g, "$1")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[(.*?)\]\((.*?)\)/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/\*(?=\s|$)/g, "")
    .replace(/\*(?=\w)/g, "")
    .replace(/(\w)\*/g, "$1")
    .replace(/#{1,6}\s/g, "")
    .replace(/>\s(.*)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
};

export const stripMarkdown = (text: string | undefined): string => {
  if (!text) return "";
  return stripMarkdownInline(String(text));
};

/** Markdown / light HTML → readable plain text with paragraph breaks (\n\n) preserved (e.g. PDF). */
export const stripMarkdownKeepParagraphs = (
  text: string | undefined,
): string => {
  if (!text) return "";
  let s = String(text).replace(/\r\n/g, "\n");
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<\/p>\s*/gi, "\n\n");
  s = s.replace(/<p[^>]*>/gi, "");
  return s
    .split(/\n{2,}/)
    .map((p) => stripMarkdownInline(p.replace(/\n/g, " ")))
    .filter(Boolean)
    .join("\n\n");
};

/**
 * True for Firestore modular `FieldValue` sentinels (`deleteField`, `serverTimestamp`, …).
 * The lite and full SDK entry points use distinct `FieldValue` constructors, so both are checked.
 */
function isFirestoreFieldValue(value: unknown): boolean {
  return value instanceof FieldValueLite || value instanceof FieldValueFull;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (Object.prototype.toString.call(value) !== "[object Object]") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Removes Firestore `FieldValue` sentinels from a tree so it can be JSON-snapshotted
 * next to plain Formik state. Sentinels stringify inconsistently and omit keys that
 * still exist only on the write payload (e.g. `deleteField()` for `mode`).
 */
export function stripFirestoreSentinelsForSnapshot<T>(value: T): T {
  return stripFirestoreSentinelsInner(value);
}

function stripFirestoreSentinelsInner<T>(value: T): T {
  if (isFirestoreFieldValue(value)) {
    return undefined as T;
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) =>
      isFirestoreFieldValue(item)
        ? undefined
        : stripFirestoreSentinelsInner(item),
    ) as T;
  }
  if (!isPlainObject(value)) {
    return value;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (isFirestoreFieldValue(v)) continue;
    out[k] = stripFirestoreSentinelsInner(v);
  }
  return out as T;
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export function diffObjects<T extends object | null | undefined>(
  obj1: T,
  obj2: T,
  ignore: string[] = [],
): any {
  if (Object.is(obj1, obj2)) {
    return undefined;
  }

  // Handle arrays by iterating over the maximum length.
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    const len1 = obj1.length;
    const len2 = obj2.length;
    const maxLen = Math.max(len1, len2);
    const result: (T | null)[] = [];
    let changed = len1 !== len2; // length changes imply a change

    for (let i = 0; i < maxLen; i++) {
      // Item removed in obj2
      if (i >= len2) {
        // Mark removed indices explicitly as undefined to signal deletion
        // biome-ignore lint/suspicious/noExplicitAny: generic diff representation
        (result as any)[i] = undefined;
        changed = true;
        continue;
      }
      // Item added in obj2 when i >= len1 is handled by diffObjects(undefined, obj2[i])
      const diffResult = diffObjects(
        // biome-ignore lint/suspicious/noExplicitAny: generic access
        (obj1 as any)[i],
        // biome-ignore lint/suspicious/noExplicitAny: generic access
        (obj2 as any)[i],
        ignore,
      );
      if (diffResult === undefined) {
        result[i] = null; // Unchanged items set to null
      } else {
        // biome-ignore lint/suspicious/noExplicitAny: generic diff representation
        (result as any)[i] = diffResult as any;
        changed = true;
      }
    }
    return changed ? result : undefined;
  }

  // If one is an array and the other is not, or one is not an object, return the new value.
  if (
    Array.isArray(obj1) ||
    Array.isArray(obj2) ||
    typeof obj1 !== "object" ||
    typeof obj2 !== "object" ||
    obj1 === null ||
    obj2 === null
  ) {
    return obj2;
  }

  // Handle objects by comparing all keys.
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const result: Record<string, any> = {};
  const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
  let changed = false;

  for (const key of keys) {
    if (ignore.includes(key)) continue;
    if (!(key in obj2)) {
      // Property was removed in obj2, explicitly set it to null
      result[key] = undefined;
      changed = true;
    } else {
      // INSERT_YOUR_REWRITE_HERE
      const diffResult = diffObjects<object | null | undefined>(
        (obj1 as Record<string, object | null>)[key],
        (obj2 as Record<string, object | null>)[key],
        ignore,
      );
      if (diffResult !== undefined) {
        result[key] = diffResult;
        changed = true;
      }
    }
  }

  return changed ? result : undefined;
}

/**
 * Finds all paths in a nested object where the value is undefined
 * @param obj - The object to search
 * @param prefix - Internal parameter for tracking the current path
 * @returns Array of paths (as strings) where values are undefined
 * @example
 * const obj = { a: 1, b: { c: undefined, d: 2 }, e: undefined };
 * findUndefinedPaths(obj); // Returns: ['b.c', 'e']
 */
export function findUndefinedPaths(
  // biome-ignore lint/suspicious/noExplicitAny: utility function needs to accept any object type
  obj: any,
  prefix = "",
): string[] {
  const paths: string[] = [];

  if (obj === null || typeof obj !== "object") {
    return paths;
  }

  for (const [key, value] of Object.entries(obj)) {
    const currentPath = prefix ? `${prefix}.${key}` : key;

    if (value === undefined) {
      paths.push(currentPath);
    } else if (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value)
    ) {
      // Recursively check nested objects (but not arrays)
      paths.push(...findUndefinedPaths(value, currentPath));
    }
  }

  return paths;
}

/**
 * Finds all paths in an object where the value is an array (immediate children only)
 * @param obj - The object to search
 * @param prefix - Internal parameter for tracking the current path
 * @param deep - If true, recursively searches nested objects for arrays (default: false)
 * @returns Array of paths (as strings) where values are arrays
 * @example
 * const obj = { a: [1, 2], b: { c: [3, 4] }, d: 'string' };
 * findNestedArrays(obj); // Returns: ['a']
 * findNestedArrays(obj, '', true); // Returns: ['a', 'b.c']
 */
export function findNestedArrays(
  // biome-ignore lint/suspicious/noExplicitAny: utility function needs to accept any object type
  obj: any,
  prefix = "",
  deep = false,
): string[] {
  const paths: string[] = [];

  if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
    return paths;
  }

  for (const [key, value] of Object.entries(obj)) {
    const currentPath = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(value)) {
      paths.push(currentPath);
    } else if (deep && typeof value === "object" && value !== null) {
      // Recursively check nested objects if deep mode is enabled
      paths.push(...findNestedArrays(value, currentPath, deep));
    }
  }

  return paths;
}

type BlockAlignment = "start" | "center" | "end" | "nearest" | number;
type InlineAlignment = "start" | "center" | "end" | "nearest" | number;

interface ScrollToRatioOptions {
  /** Scroll behavior. Default: "smooth" */
  behavior?: ScrollBehavior;
  /** Vertical alignment: keyword or ratio (0 = top, 1 = bottom). Default: "nearest" */
  block?: BlockAlignment;
  /** Horizontal alignment: keyword or ratio (0 = left, 1 = right). Default: "nearest" */
  inline?: InlineAlignment;
}

/** Find nearest scrollable ancestor, like scrollIntoView does */
function findScrollableContainer(el: HTMLElement): HTMLElement | Document {
  let parent: HTMLElement | null = el.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const canScrollY =
      (overflowY === "auto" || overflowY === "scroll") &&
      parent.scrollHeight > parent.clientHeight;
    const canScrollX =
      (overflowX === "auto" || overflowX === "scroll") &&
      parent.scrollWidth > parent.clientWidth;

    if (canScrollY || canScrollX) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document;
}

/**
 * Scrolls so that `target` lands according to block/inline,
 * where block/inline can be keywords ("start", "center", "end", "nearest")
 * or ratios (0–1).
 */
export function scrollIntoViewRatio(
  target: HTMLElement,
  opts: ScrollToRatioOptions = {},
) {
  const behavior: ScrollBehavior = opts.behavior ?? "smooth";

  const container = findScrollableContainer(target);
  const isDoc = container instanceof Document;
  const el = isDoc
    ? container.scrollingElement || document.documentElement
    : container;

  if (!el) return;

  const cRect = isDoc
    ? new DOMRect(0, 0, window.innerWidth, window.innerHeight)
    : el.getBoundingClientRect();

  const tRect = target.getBoundingClientRect();

  const currentTop = el.scrollTop;
  const currentLeft = el.scrollLeft;

  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(v, max));
  const maxTop = el.scrollHeight - cRect.height;
  const maxLeft = el.scrollWidth - cRect.width;

  const targetCenterY = currentTop + (tRect.top - cRect.top) + tRect.height / 2;
  const targetCenterX =
    currentLeft + (tRect.left - cRect.left) + tRect.width / 2;

  // --- Vertical ---
  let desiredTop: number;
  if (typeof opts.block === "number") {
    const r = clamp(opts.block, 0, 1);
    desiredTop = targetCenterY - cRect.height * r;
  } else {
    switch (opts.block) {
      case "start":
        desiredTop = currentTop + (tRect.top - cRect.top);
        break;
      case "center":
        desiredTop =
          currentTop +
          (tRect.top - cRect.top) -
          (cRect.height / 2 - tRect.height / 2);
        break;
      case "end":
        desiredTop = currentTop + (tRect.bottom - cRect.bottom);
        break;
      case "nearest":
      default: {
        const topDelta = tRect.top - cRect.top;
        const bottomDelta = tRect.bottom - cRect.bottom;
        desiredTop =
          Math.abs(topDelta) < Math.abs(bottomDelta)
            ? currentTop + topDelta
            : currentTop + bottomDelta;
      }
    }
  }
  desiredTop = clamp(desiredTop, 0, maxTop);

  // --- Horizontal ---
  let desiredLeft: number;
  if (typeof opts.inline === "number") {
    const r = clamp(opts.inline, 0, 1);
    desiredLeft = targetCenterX - cRect.width * r;
  } else {
    switch (opts.inline) {
      case "start":
        desiredLeft = currentLeft + (tRect.left - cRect.left);
        break;
      case "center":
        desiredLeft =
          currentLeft +
          (tRect.left - cRect.left) -
          (cRect.width / 2 - tRect.width / 2);
        break;
      case "end":
        desiredLeft = currentLeft + (tRect.right - cRect.right);
        break;
      case "nearest":
      default: {
        const leftDelta = tRect.left - cRect.left;
        const rightDelta = tRect.right - cRect.right;
        desiredLeft =
          Math.abs(leftDelta) < Math.abs(rightDelta)
            ? currentLeft + leftDelta
            : currentLeft + rightDelta;
      }
    }
  }
  desiredLeft = clamp(desiredLeft, 0, maxLeft);

  el.scrollTo({ top: desiredTop, left: desiredLeft, behavior });
}

/**
 * Safely parse environment variables to booleans
 * @param key - Environment variable key
 * @param fallback - Default value if key is undefined
 * @returns Parsed boolean value
 */
export function readEnvBool(key: string, fallback = false): boolean {
  // Try import.meta.env first (browser/Vite context), then fallback to process.env (Node.js/Netlify)
  let v = (import.meta as any)?.env?.[key];
  if (v == null && typeof process !== "undefined" && process.env) {
    v = process.env[key];
  }
  if (v == null) return fallback;

  // Handle built-in Vite booleans (PROD, DEV, SSR)
  if (v === true) return true;
  if (v === false) return false;

  // Handle custom VITE_* variables (always strings)
  const s = String(v).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes" || s === "on") return true;
  if (s === "false" || s === "0" || s === "no" || s === "off") return false;
  return fallback;
}

export const isUuid = (str: string): boolean => {
  const uuidV4Regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidV4Regex.test(str);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};
